import React from "react";
import { Link } from "react-router-dom";

function Home() {
  return (
    <div className="landing">
      <style>{`
        .landing {
          --bg: #0b0f14;
          --muted: #93a4b3;
          --text: #e5e7eb;
          --card: rgba(255, 255, 255, 0.06);
          --border: rgba(255, 255, 255, 0.09);
          --accent: #22c55e; /* Olin green */
          min-height: 100vh;
          background:
            radial-gradient(40% 50% at 85% 10%, rgba(34, 197, 94, 0.15) 0%, rgba(0,0,0,0) 60%),
            radial-gradient(30% 35% at 10% 20%, rgba(34, 197, 94, 0.1) 0%, rgba(0,0,0,0) 60%),
            linear-gradient(180deg, #0b0f14 0%, #070b10 60%, #05070a 100%);
          color: var(--text);
        }

        .container { max-width: 1100px; margin: 0 auto; padding: 24px; }

        .nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 0; position: sticky; top: 0; backdrop-filter: blur(6px);
        }
        .logo { display: flex; align-items: center; gap: 10px; font-weight: 700; }
        .logoMark {
          width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
          background: linear-gradient(135deg, rgba(34,197,94,.3), rgba(34,197,94,.05));
          border: 1px solid var(--border);
          box-shadow: 0 10px 30px rgba(34,197,94,.12) inset, 0 4px 30px rgba(0,0,0,.35);
        }
        .logoMark img { width: 22px; height: 22px; object-fit: contain; display: block; }
        .navActions { display: flex; align-items: center; gap: 10px; }

        .badge {
          display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px;
          border-radius: 999px; font-size: 12px; color: var(--text);
          background: var(--card); border: 1px solid var(--border);
        }

        .hero { text-align: center; padding: 96px 0 48px; }
        .title {
          font-size: clamp(32px, 6vw, 64px); line-height: 1.04; letter-spacing: -0.02em;
          margin: 16px 0 16px;
          background: linear-gradient(180deg, #ffffff, #b9c2ca 60%, #7b8794);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .subtitle { max-width: 760px; margin: 0 auto; color: var(--muted); font-size: 18px; }

        .ctaGroup { display: flex; justify-content: center; gap: 12px; margin-top: 28px; }
        .btn {
          display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 999px;
          border: 1px solid var(--border); background: var(--card); color: var(--text);
          text-decoration: none; transition: transform .15s ease, background .2s ease, border-color .2s ease;
        }
        .btn:hover { transform: translateY(-1px); border-color: rgba(255,255,255,.2); }
        .btnPrimary { background: linear-gradient(180deg, #2fe26f, #16a34a); color: #061007; border: none; font-weight: 700; }
        .btnSecondary { }

        .hint { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 18px; color: var(--muted); font-size: 13px; }

        .features { padding: 28px 0 72px; }
        .grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 16px; }
        .card {
          grid-column: span 4;
          background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 18px; height: 100%;
        }
        @media (max-width: 900px) { .card { grid-column: span 12; } }
        .cardTitle { font-size: 18px; margin: 2px 0 8px; }
        .cardText { color: var(--muted); font-size: 14px; }
        .chip { display: inline-block; font-size: 11px; padding: 4px 8px; border-radius: 999px; background: rgba(34,197,94,.12); color: #86efac; border: 1px solid rgba(34,197,94,.35); }

        .partners { display: flex; gap: 18px; flex-wrap: wrap; justify-content: center; opacity: .75; font-size: 13px; margin-top: 36px; color: var(--muted); }

        .footer { padding: 48px 0; }
        .footerNav { display: flex; gap: 16px; justify-content: center; color: var(--muted); }
        .footerNav a { color: var(--muted); text-decoration: none; }
        .footerNav a:hover { color: #cbd5e1; }
      `}</style>

      <div className="container">
        <header className="nav">
          <div className="logo">
            <div className="logoMark">
              <img src="/icon-orbi-transparent.png" alt="Olin logo" />
            </div>
            <span>Olin</span>
          </div>
          <div className="navActions">
            <a className="btn" href="#about">
              Discover More
            </a>
            <a
              className="btn btnPrimary"
              href="https://linktr.ee/Olin.Finance"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open App
            </a>
          </div>
        </header>

        <main className="hero">
          <span className="badge">Unlock calm, collaborative finances</span>
          <h1 className="title">Manage money together without friction</h1>
          <p className="subtitle">
            Olin is a collaborative personal finance app for couples, families,
            and friends. Track shared expenses, set goals, and follow collective
            progress with clarity.
          </p>
          <div className="ctaGroup">
            <a
              className="btn btnPrimary"
              href="https://linktr.ee/Olin.Finance"
              target="_blank"
              rel="noopener noreferrer"
            >
              Create Account
            </a>
            <a className="btn btnSecondary" href="#about">
              How Olin works
            </a>
          </div>
          <div className="hint">↓ Scroll for features</div>

          <div className="partners">
            <span>Trusted by thoughtful savers</span>
            <span>•</span>
            <span>Designed for peace of mind</span>
          </div>
        </main>

        <section id="about" className="features">
          <div className="grid">
            <div className="card">
              <span className="chip">Together</span>
              <h3 className="cardTitle">Collaborative by design</h3>
              <p className="cardText">
                Share a space for spending, agreements, and context—built for
                couples, families, or roommates.
              </p>
            </div>
            <div className="card">
              <span className="chip">Transparent</span>
              <h3 className="cardTitle">Clear, fair tracking</h3>
              <p className="cardText">
                Log expenses once, see who owes what, and keep everything
                organized without spreadsheets.
              </p>
            </div>
            <div className="card">
              <span className="chip">Motivating</span>
              <h3 className="cardTitle">Goals & progress</h3>
              <p className="cardText">
                Set targets, visualize progress, and celebrate the wins—small
                changes compound into big results.
              </p>
            </div>
          </div>
        </section>

        <footer className="footer">
          <nav className="footerNav">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/reset-password">Reset Password</Link>
            <Link to="/delete-account">Delete Account</Link>
          </nav>
        </footer>
      </div>
    </div>
  );
}

export default Home;
